import { HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { PropertyDetailsSnapshotService } from '@root/shared-report-platform/services/property-details-snapshot.service';
import { SharedLinkApiService } from '@root/shared-report-platform/services/shared-link-api.service';
import {
  cancelPropertyDetailsShare,
  generatePropertyDetailsShare,
  propertyDetailsShareFailed,
  propertyDetailsShareSucceeded
} from '@store/property-details-share/property-details-share.actions';
import { LOGOUT_SUCCESS } from '@store/user/user.actions';
import { defer, merge, of } from 'rxjs';
import { catchError, filter, map, switchMap, take, takeUntil, throwIfEmpty } from 'rxjs/operators';

const fallbackMessage = 'Unable to generate the share link. Please try again.';

// Only known, agent-facing service messages may be shown; arbitrary exception details stay private.
const preparationMessages = new Set([
  'A property identifier is required to capture Property Details.',
  'Property Details must be loaded without an error or required purchase.',
  'A property identifier is required to create a shared link.',
  'At least one report snapshot is required to create a shared link.',
  'The shared link response must contain a valid HTTP(S) URL.',
  'An image source is required.',
  'The image response must be a nonempty PNG, JPEG, GIF or WebP.',
  'Unsupported or malformed embedded image.',
  'Malformed base64 image.',
  'Malformed encoded image.',
  'Malformed image URL.',
  'Image URLs must be root-relative or HTTP(S).',
  'The image reader did not return a data URI.',
  'Image reading failed.',
  'Image reading was aborted.'
]);

function errorMessage(error: unknown): string {
  if (error instanceof HttpErrorResponse) {
    switch (error.status) {
      case 0:
        return 'Unable to connect. Check your connection and try again.';
      case 401:
        return 'Your session has expired. Sign in again to share this report.';
      case 403:
        return 'Sharing reports is not enabled for your account.';
      case 413:
        return 'This report is too large to share.';
      case 429:
        return 'Too many requests. Please wait before trying again.';
      default:
        return fallbackMessage;
    }
  }
  return error instanceof Error && preparationMessages.has(error.message) ? error.message : fallbackMessage;
}

@Injectable()
export class PropertyDetailsShareEffects {
  generate$ = createEffect(() => this.actions$.pipe(
    ofType(generatePropertyDetailsShare),
    switchMap(({ requestId, capture }) =>
      defer(() => this.snapshots.prepareSnapshot(capture)).pipe(
        take(1),
        throwIfEmpty(),
        switchMap(envelope =>
          defer(() => this.sharedLinks.createSharedLink(capture.propertyInformation, [envelope])).pipe(
            take(1),
            throwIfEmpty()
          )
        ),
        map(response => propertyDetailsShareSucceeded({ requestId, response })),
        catchError((error: unknown) => of(propertyDetailsShareFailed({ requestId, errorMessage: errorMessage(error) }))),
        takeUntil(merge(
          this.actions$.pipe(
            ofType(cancelPropertyDetailsShare),
            filter(action => action.requestId === requestId)
          ),
          this.actions$.pipe(ofType(LOGOUT_SUCCESS))
        ))
      )
    )
  ));

  constructor(
    private readonly actions$: Actions,
    private readonly snapshots: PropertyDetailsSnapshotService,
    private readonly sharedLinks: SharedLinkApiService
  ) {}
}
